// eslint-disable-next-line no-unused-vars
import React, { memo, useEffect, useState, useMemo } from 'react';
import { stringify } from 'query-string';
import { useForm } from 'react-hook-form';
import { omit } from 'lodash';
import { useTranslation } from 'react-i18next';
import { array, func, object } from 'prop-types';

import useRouter from 'Hooks/useRouter';
import useUser from 'Hooks/useUser';
import useFetch from 'Hooks/useFetch';
import useScrollLock from 'Hooks/useScrollLock';
import FilterOrdersComponent from 'Components/Reports/FilterReports';
import download from 'Utils/download';
import useAnalytics from 'Hooks/useAnalytics';
import {
  API_INVOICE_SYNCHRONIZATION,
  API_INVOICE_REFRESH,
  API_INVOICES_FEED,
  API_UNPAID_INVOICES_FEED,
  API_PAYMENTS_FEED,
} from 'Constants';

const FilterReports = memo(({
  sortOptions,
  currentSort,
  handleMobileOrderChange,
  getTableData,
}) => {
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [mobileSortOpen, setMobileSortOpen] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  const [timeLeft, setTimeLeft] = useState(0);
  const { t } = useTranslation();
  const { push, location, query, history } = useRouter();
  const { data: { loggedIn } } = useUser();
  const fetcher = useFetch();
  const analytics = useAnalytics();
  const {
    register,
    control,
    reset,
    watch,
    getValues,
    setValue,
  } = useForm();

  const startDate = watch('invoice_date_start');
  const endDate = watch('invoice_date_end');
  const documentStartDate = watch('document_date_start');
  const documentEndDate = watch('document_date_end');
  const dueDateStart = watch('due_date_start');
  const dueDateEnd = watch('due_date_end');
  const paymentsStart = watch('date_start');
  const paymentsEnd = watch('date_end');

  const states = useMemo(() => [
    { value: '1', label: t('orders.states.all') },
    { value: '2', label: t('orders.states.unpaidInvoices') },
    { value: '3', label: t('orders.states.paidInvoices') },
    { value: '4', label: t('orders.states.overdueInvoices') },
  ], []);

  useEffect(() => {
    let timeoutId;

    if (loggedIn && Object.keys(omit(query || {}, ['order_by', 'order_direction', 'page'])).length) {
      timeoutId = setTimeout(() => {
        Object.entries(query).forEach(([key, value]) => {
          setValue(key, value);
        });
      }, 500);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [query, loggedIn]);

  useScrollLock(mobileFiltersOpen);

  useEffect(() => {
    fetchTimerData();
  }, []);

  useEffect(() => {
    if (timeLeft <= 0) return;

    const intervalId = setInterval(() => {
      setTimeLeft(prevTime => prevTime - 1);
    }, 1000);

    // eslint-disable-next-line consistent-return
    return () => clearInterval(intervalId);
  }, [timeLeft]);

  useEffect(() => {
    if (query && history.pathname !== location.pathname) {
      reset({
        invoice_number: '',
        purchase_order: '',
        product_code: '',
        status: null,
        value_from: '',
        value_to: '',
        iban: '',
      });
    }
  }, [location?.pathname]);

  const handleSubmit = () => {
    setMobileFiltersOpen(false);

    const formValues = getValues();

    const queryParams = Object.entries(formValues)
      // eslint-disable-next-line no-unused-vars
      .filter(([_, value]) => value)
      .reduce((acc, [key, value]) => {
        if (value !== 'false') {
          acc[key] = value;
        }
        return acc;
      }, {});

    const searchedValues = Object.keys(queryParams).join(', ');
    analytics.track('filter_reports', { filters: searchedValues });

    push({
      location: location.pathname,
      search: stringify(queryParams)
    });
  };

  const handleClearFilters = () => {
    analytics.track('clear_filters');

    setMobileFiltersOpen(false);
    reset({
      invoice_number: '',
      purchase_order: '',
      product_code: '',
      status: null,
      value_from: '',
      value_to: '',
      iban: '',
    });

    push({
      location: location.pathname,
      search: ''
    });
  };

  const handleDateChange = (key, date) => {
    if (!date || !date.length) {
      setValue(key, '');
      return;
    }

    const originalDate = new Date(date);

    let day = originalDate.getDate();
    let month = originalDate.getMonth() + 1; // Adding 1 because months are zero-indexed
    const year = originalDate.getFullYear();

    // Format day and month to have leading zeros if needed
    day = (day < 10) ? `0${ day }` : day;
    month = (month < 10) ? `0${ month }` : month;

    const formattedDate = `${ year }-${ month }-${ day }`;

    setValue(key, formattedDate);
  };

  const onMobileOrderChange = sort => {
    analytics.track('mobile_sort');
    setMobileSortOpen(false);
    handleMobileOrderChange(sort);
  };

  const handleClearFilter = key => {
    analytics.track('filters_clear');
    setValue(key, '');
    push({
      location: location.pathname,
      search: stringify(omit(query, [key]))
    });
  };

  const fetchTimerData = async () => {
    const res = await fetcher(API_INVOICE_REFRESH);
    const [minutes, seconds] = (res?.buttonExpiration || '00m 00s').split(' ').map(part => parseInt(part, 10));
    const totalSeconds = minutes * 60 + seconds;
    setTimeLeft(totalSeconds);
  };

  const handleSyncInvoices = () => {
    analytics.track('sync_invoices');
    setSyncLoading(true);
    fetcher(`${ API_INVOICE_SYNCHRONIZATION }`, { method: 'POST', body: JSON.stringify({}) })
      .then(res => {
        fetchTimerData();
        if (res?.count) {
          getTableData();
          setErrors([{ message: t('orders.invoiceSyncSuccess', { count: res.count }), type: 'success' }]);
        } else {
          setErrors([{ message: t('orders.invoiceSyncWarning'), type: 'success' }]);
        }
      })
      .catch(err => console.error('API_INVOICE_SYNCHRONIZATION', err))
      .finally(() => setSyncLoading(false));
  };

  const formatTime = seconds => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleDownload = fileType => {
    analytics.track('file_download', { type: fileType });

    let path = '';
    const newQuery = { format: fileType, ...query };

    if (location.pathname === '/reports/invoices') {
      path = API_INVOICES_FEED;

      if (query.status === '2') {
        newQuery.is_paid = 0;
      }

      if (query.status === '3') {
        newQuery.is_paid = 1;
      }

      if (query.status === '4') {
        newQuery.is_overdue = 1;
      }
    }

    if (location.pathname === '/reports/unpaid-invoices') {
      path = API_INVOICES_FEED;
      newQuery.status = 2;
      newQuery.is_paid = 0;
    }

    if (location.pathname === '/reports/balance') {
      path = API_UNPAID_INVOICES_FEED;
    }

    if (location.pathname === '/reports/payments') {
      path = API_PAYMENTS_FEED;
    }

    download(`${ path }?${ stringify({ ...newQuery }) }`);
  };

  return (
    <FilterOrdersComponent
      register={ register }
      control={ control }
      getValues={ getValues }
      setValue={ setValue }
      onSubmit={ handleSubmit }
      onClearFilters={ handleClearFilters }
      onClearFilter={ handleClearFilter }
      onChangeDate={ handleDateChange }
      states={ states }
      startDate={ startDate }
      endDate={ endDate }
      documentStartDate={ documentStartDate }
      documentEndDate={ documentEndDate }
      dueDateStart={ dueDateStart }
      dueDateEnd={ dueDateEnd }
      paymentsStart={ paymentsStart }
      paymentsEnd={ paymentsEnd }
      activeFilters={ omit(query || {}, ['order_by', 'order_direction', 'page']) }
      mobileFiltersOpen={ mobileFiltersOpen }
      setMobileFiltersOpen={ setMobileFiltersOpen }
      mobileSortOpen={ mobileSortOpen }
      setMobileSortOpen={ setMobileSortOpen }
      sortOptions={ sortOptions }
      currentSort={ currentSort }
      handleMobileOrderChange={ onMobileOrderChange }
      onSyncInvoices={ handleSyncInvoices }
      onDownload={ handleDownload }
      syncLoading={ syncLoading }
      timeToDisplay={ formatTime(timeLeft) }
      errors={ errors }
      locationPathname={ location.pathname }
    />
  );
});

FilterReports.propTypes = {
  sortOptions: array,
  currentSort: object,
  handleMobileOrderChange: func,
  getTableData: func,
};

FilterReports.displayName = 'FilterReportsContainer';

export default FilterReports;
